import {useEffect, useMemo, useRef, useState} from "react";
import {useOrganisations} from "../../hooks/PayLevelsSearches";
import _ from "lodash";
import {defaultDataGridWithSelectProperties, formatCompanyName} from "../utils/DataGridUtils";
import {MaterialReactTable, useMaterialReactTable} from "material-react-table";
import OrganizationDataGridDetailPanel from "./OrganisationDataGridDetailPanel";
import {Stack} from "@mui/material";
import DataGridHeader from "../DataGridHeader";

const formatSalary = (value) => {
    if (value) {
        return value.toLocaleString(undefined, {style: "currency", currency: "GBP"});
    }
    return "-";
};

const availableItems = (data) => {
    return data ? data.map((item) => item.id) : [];
};

const OrganisationDataGrid = ({filters, bouncedFilters, updateFilters}) => {
    const [organisations, setOrganisations] = useState([]);
    const [rowSelectionModel, setRowSelectionModel] = useState({});
    const isFirstRender = useRef(true);

    const {data, isLoading} = useOrganisations(bouncedFilters);

    useEffect(() => {
        if (!isLoading) {
            setOrganisations(data);

            const shouldBeSelected = _.difference(availableItems(data), filters.ignoredSalaries);

            if (isFirstRender.current && shouldBeSelected.length > 0) {
                const initialRowSelectionModel = shouldBeSelected.reduce((row, id) => {
                    row[id] = true;
                    return row;
                }, {});

                setRowSelectionModel(initialRowSelectionModel);
                isFirstRender.current = false;
            }
        }
    }, [data, isLoading, filters, organisations]);

    const columns = useMemo(() => [
            {
                accessorKey: "organisation",
                header: "Organisation",
                Cell: ({renderedCellValue}) => (<>{formatCompanyName(renderedCellValue)}</>),
                size: 200,
                sortingFn: "organisationSort",
            },
            {
                accessorKey: "jobRole",
                header: "Job Role",
            },
            {
                accessorKey: "jobTitle",
                header: "Job Title",
            },
            {
                accessorKey: "jobLevel",
                header: "Job Level",
                muiTableBodyCellProps: {
                    align: 'center',
                },
                size: 114,
                grow: false,
            },
            {
                accessorKey: "nationalRate",
                header: "Annual Salary",
                Cell: ({renderedCellValue}) => (<>{formatSalary(renderedCellValue)}</>),
                muiTableBodyCellProps: {
                    align: 'right',
                },
                size: 143,
                grow: false,
            },
            {
                accessorKey: "salaryMin",
                header: "Min",
                Cell: ({renderedCellValue}) => (<>{formatSalary(renderedCellValue)}</>),
                muiTableBodyCellProps: {
                    align: 'right',
                },
                size: 85,
                grow: false,
            },
            {
                accessorKey: "salaryMid",
                header: "Mid",
                Cell: ({renderedCellValue}) => (<>{formatSalary(renderedCellValue)}</>),
                muiTableBodyCellProps: {
                    align: 'right',
                },
                size: 85,
                grow: false,
            },
            {
                accessorKey: "salaryMax",
                header: "Max",
                Cell: ({renderedCellValue}) => (<>{formatSalary(renderedCellValue)}</>),
                muiTableBodyCellProps: {
                    align: 'right',
                },
                size: 85,
                grow: false,
            },
            {
                accessorKey: "hoursPerWeek",
                header: "Hours Per Week",
                muiTableBodyCellProps: {
                    align: 'center',
                },
                size: 157,
            },
            {
                accessorKey: "paymentFrequency",
                header: "Pay Frequency",
                muiTableBodyCellProps: {
                    align: 'center',
                },
                size: 149,
            },
            {
                accessorKey: "daysHoliday",
                header: "Holidays",
                muiTableBodyCellProps: {
                    align: 'center',
                },
                size: 85,
            },
            {
                accessorKey: "holidayComments",
                header: "Comments",
            },
            {
                accessorKey: "shifts",
                header: "Shifts",
            },
            {
                accessorKey: "extras",
                header: "Extras",
            },
            {
                accessorKey: "footnote",
                header: "Footnote",
            },
        ],
        []);

    const table = useMaterialReactTable({
        ...defaultDataGridWithSelectProperties,
        columns: columns,
        data: organisations,
        initialState: {
            ...defaultDataGridWithSelectProperties.initialState,
            sorting: [{id: "organisation", desc: false}],
            columnVisibility: {
                jobLevel: false,
                nationalRate: false,
                salaryMin: false,
                salaryMid: false,
                salaryMax: false,
                hoursPerWeek: false,
                paymentFrequency: false,
                daysHoliday: false,
                holidayComments: false,
                shifts: false,
                extras: false,
                footnote: false,
            },
        },
        state: {
            rowSelection: rowSelectionModel,
        },
        onRowSelectionChange: setRowSelectionModel,
        sortingFns: {
            organisationSort: (row1, row2) => {
                const organisation1 = row1.original.organisation;
                const organisation2 = row2.original.organisation;
                if (organisation1 === null || organisation2 === null) {
                    if (organisation1 === organisation2) {
                        return 0;
                    }

                    return organisation1 == null ? 1 : -1;
                }

                return organisation1.localeCompare(organisation2);
            }
        },
        renderDetailPanel: ({row}) => <OrganizationDataGridDetailPanel row={row.original} formatSalary={formatSalary} />,
    });

    useEffect(() => {
        const available = availableItems(organisations);
        if (available.length > 0) {
            const currentlyIgnored = filters.ignoredSalaries;
            const selected = table.getSelectedRowModel().rows.map((row) => row.id);
            const ignored = _.difference(available, selected);

            if (currentlyIgnored.length !== ignored.length) {
                filters.ignoredSalaries = ignored;
                updateFilters();
            }
        }
    }, [table, rowSelectionModel, organisations, filters, updateFilters]);

    return (
        <Stack id="organisation-data" name="organisation-data">
            <DataGridHeader table={table}/>
            <MaterialReactTable table={table}/>
        </Stack>
    );
};

export default OrganisationDataGrid;
